import { roundToPrecision } from '../helpers/math'
import { hasValue } from '../helpers/nullable'

export type ProductionRate = {
  rateUsageQuantity: number
  units: string
  rateInstallationQuantity: number
  ratePerUnits: string
}

export type EnteredRateQuantities = Record<string, number | null>

export type DistinctRateUnit = {
  key: string
  label: string
  itemCount: number
}

export type QuantityChange = {
  guid: string
  name: string
  units: string
  currentQuantity: number
  newQuantity: number
}

export type TemplateGroup<T> = {
  templateId?: number
  items: T[]
}

export type LabeledTemplateGroup<T> = {
  key: string
  name: string
  items: T[]
}

export type TemplateName = {
  id: number
  name: string
}

export const NO_TEMPLATE_RATES_MESSAGE = 'None of the items in this work area have a template production rate'

export const calculateUsageQuantity = (installationQuantity: number, rateInstallationQuantity: number, rateUsageQuantity: number): number => {
  if (rateInstallationQuantity === 0) {
    return 0
  }
  return roundToPrecision((installationQuantity / rateInstallationQuantity) * rateUsageQuantity, 2)
}

export const normalizeRateUnits = (units: string): string => {
  return units.trim().toLowerCase()
}

export const rateEntryKey = (templateId: number | undefined, units: string): string => {
  return `${templateId ?? 'unknown'}|${normalizeRateUnits(units)}`
}

export const distinctRateUnits = (templateId: number | undefined, ratePerUnitsValues: string[]): DistinctRateUnit[] => {
  const units: DistinctRateUnit[] = []

  ratePerUnitsValues.forEach(value => {
    if (normalizeRateUnits(value) === '') {
      return
    }

    const key = rateEntryKey(templateId, value)
    const existing = units.find(x => x.key === key)
    if (existing) {
      existing.itemCount += 1
    } else {
      units.push({ key, label: value.trim(), itemCount: 1 })
    }
  })

  return units.sort((a, b) => a.key.localeCompare(b.key))
}

export const labelTemplateGroups = <T>(groups: TemplateGroup<T>[], templateNames: TemplateName[]): LabeledTemplateGroup<T>[] => {
  return groups
    .map(group => {
      const name = hasValue(group.templateId)
        ? templateNames.find(x => x.id === group.templateId)?.name
        : undefined

      return {
        group: {
          key: group.templateId?.toString() ?? 'unknown',
          name: name ?? 'Other items',
          items: group.items,
        },
        isNamed: hasValue(name),
      }
    })
    .sort((a, b) => {
      if (a.isNamed !== b.isNamed) {
        return a.isNamed ? -1 : 1
      }
      return a.group.name.localeCompare(b.group.name) || a.group.key.localeCompare(b.group.key)
    })
    .map(x => x.group)
}

export const hasAnyEnteredQuantity = (entered: EnteredRateQuantities): boolean => {
  return Object.values(entered).some(hasValue)
}

export const isChangeSelected = (excludedGuids: string[], guid: string): boolean => {
  return !excludedGuids.includes(guid)
}

export const toggleChangeSelection = (excludedGuids: string[], guid: string, isSelected: boolean): string[] => {
  if (isSelected) {
    return excludedGuids.filter(x => x !== guid)
  }
  return isChangeSelected(excludedGuids, guid) ? [...excludedGuids, guid] : excludedGuids
}

export type TemplateRatesButtonState = {
  isDisabled: boolean
  reason: string
}

export const getTemplateRatesButtonState = (isActualItemsTab: boolean, isLoadingItems: boolean, groupCount: number): TemplateRatesButtonState => {
  if (isActualItemsTab) {
    return { isDisabled: true, reason: 'Production rates apply to estimated items only' }
  }
  if (isLoadingItems) {
    return { isDisabled: true, reason: '' }
  }
  if (groupCount < 1) {
    return { isDisabled: true, reason: NO_TEMPLATE_RATES_MESSAGE }
  }
  return { isDisabled: false, reason: '' }
}

export const getApplyDisabledReason = (changeCount: number, selectedCount: number): string => {
  if (changeCount < 1) {
    return 'These amounts would not change any quantities'
  }
  if (selectedCount < 1) {
    return 'Check at least one item to apply'
  }
  return ''
}

export const getSelectedChanges = (groups: LabeledTemplateGroup<QuantityChange>[], excludedGuids: string[]): QuantityChange[] => {
  return groups
    .flatMap(x => x.items)
    .filter(x => isChangeSelected(excludedGuids, x.guid))
}
